import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Dropdown,
  DropdownValue,
  FormLabel,
  Loading,
} from '@pandiafi/design-system'
import { Alert } from '@pandiafi/components'

import { MessageCategory } from '../../../../common/validations/residentMessageTimelineSchema'
import { getMessageCategoryLabel } from '../utils'

import FormError from 'components/FormError'

type MessageCategoryFieldProps = {
  categories: MessageCategory[]
  disabled: boolean
  isLoading: boolean
  isError: boolean
  onRetry: () => void
  selectedCategory: MessageCategory | null | undefined
  value: string
  validationError?: string
  onChange: (value: string) => void
}

export const MessageCategoryField: React.FC<MessageCategoryFieldProps> = ({
  categories,
  disabled,
  isLoading,
  isError,
  onRetry,
  selectedCategory,
  value,
  validationError,
  onChange,
}) => {
  const { t, i18n } = useTranslation(['messages'])
  const hasSetDefaultCategoryRef = useRef(false)

  useEffect(() => {
    if (
      !disabled &&
      !hasSetDefaultCategoryRef.current &&
      !value &&
      categories[0]
    ) {
      hasSetDefaultCategoryRef.current = true
      onChange(String(categories[0].id))
    }
  }, [categories, disabled, onChange, value])

  const options: DropdownValue<string>[] = [
    ...(disabled && !selectedCategory
      ? [{ id: '', label: t('messages:form.noCategory') }]
      : []),
    ...categories.map((category) => ({
      id: String(category.id),
      label: getMessageCategoryLabel(category, i18n.language),
    })),
    ...(selectedCategory &&
    !categories.some(({ id }) => Object.is(id, selectedCategory.id))
      ? [
          {
            id: String(selectedCategory.id),
            label: getMessageCategoryLabel(selectedCategory, i18n.language),
          },
        ]
      : []),
  ]

  return (
    <div>
      <FormLabel for="messageCategoryId" required={!disabled}>
        {t('messages:form.category')}
      </FormLabel>
      {isLoading ? (
        <div className="text-center">
          <Loading />
        </div>
      ) : isError ? (
        <div className="space-y-4">
          <Alert variant="warning">
            {t('messages:form.categoryLoadError')}
          </Alert>
          <Button type="button" onClick={onRetry}>
            {t('messages:form.retry')}
          </Button>
        </div>
      ) : (
        <Dropdown
          id="messageCategoryId"
          value={options.find((option) => Object.is(option.id, value)) ?? null}
          options={options}
          disabled={disabled}
          placeholder={t('messages:form.selectCategory')}
          onChange={(option) => onChange(option?.id ?? '')}
        />
      )}
      {validationError && (
        <div aria-live="assertive" aria-atomic="true">
          <FormError
            name="messageCategoryId"
            errors={{ messageCategoryId: validationError }}
          />
        </div>
      )}
    </div>
  )
}
